import { useState } from 'react'

import { useApolloClient } from '@apollo/client'
import HistoryIcon from '@mui/icons-material/History'
import { Button, Tooltip } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'

import { SelectTopBookingDialog } from 'components/bookings'
import {
  BookingAddressType,
  GetBookingDocument,
  useGetTopCustomerBookingsQuery,
} from 'generated/graphql'
import { mapToBookingAddressForm, mapToBookingChargeForm, mapToBookingPackageForm } from 'helpers'
import { TBookingForm } from 'types'

export const BookingHistoryButton = () => {
  const [openSelectTopBookingDialog, setOpenSelectTopBookingDialog] = useState(false)
  const client = useApolloClient()
  const { reset } = useFormContext<TBookingForm>()
  const customerIdValue = useWatch({ name: 'customerId' })

  const { data, loading } = useGetTopCustomerBookingsQuery({
    fetchPolicy: 'network-only',
    variables: {
      customerId: Number(customerIdValue),
    },
  })

  const topBookingList = data?.topCustomerBookings

  // handlers
  const handleSelectTopBooking = async (bookingId: number) => {
    try {
      const { data } = await client.query({
        query: GetBookingDocument,
        variables: { bookingId },
        fetchPolicy: 'network-only',
      })

      const booking = data?.booking
      if (!booking) return

      const {
        customerId,
        customerReference,
        contactId,
        overrideContactEmail,
        overrideContactTelephoneNumber,
        validUntil,
        sendQuoteEmail,
        addresses,
        distance,
        returnDistance,
        packages,
        tariffId,
        isWaitAndReturn,
        charges,
        noteOrSpecialInstruction,
        alternativeOurReference,
        consignmentFee,
        isEmailNotification,
        serviceType,
        isTransitIgnored,
        isBiddingEnabled,
        status,
        isReferenceRequired,
        isReasonCodeEnabled,
        isPackageDetailsSectionOptional,
        consignmentFeeFromTariff,
        consignmentFeeFromDynamicPricing,
        waitAndReturnFeeCategoryId,
      } = booking

      const collectionAddresses = addresses
        .filter((addr: any) => addr.type === BookingAddressType.Collection)
        .map(mapToBookingAddressForm)

      const deliveryAddresses = addresses
        .filter((addr: any) => addr.type === BookingAddressType.Delivery)
        .map(mapToBookingAddressForm)

      reset({
        customerId: String(customerId),
        customerReference: customerReference ?? '',
        contactId: String(contactId),
        overrideContactEmail: overrideContactEmail ?? '',
        overrideContactTelephoneNumber: overrideContactTelephoneNumber ?? '',
        validUntil,
        sendQuoteEmail,
        collectionAddresses,
        deliveryAddresses,
        distance,
        returnDistance,
        packages: packages?.map(mapToBookingPackageForm) || [],
        tariffId: String(tariffId),
        isWaitAndReturn,
        charges: charges?.map(mapToBookingChargeForm) || [],
        noteOrSpecialInstruction: noteOrSpecialInstruction ?? '',
        alternativeOurReference: alternativeOurReference ?? '',
        consignmentFee,
        isEmailNotification,
        serviceType,
        isTransitIgnored,
        isBiddingEnabled,
        status,
        isReferenceRequired,
        isReasonCodeEnabled,
        isPackageDetailsSectionOptional,
        consignmentFeeFromTariff: consignmentFeeFromTariff ?? 0,
        consignmentFeeFromDynamicPricing: consignmentFeeFromDynamicPricing ?? 0,
        waitAndReturnFeeCategoryId: String(waitAndReturnFeeCategoryId),
      })

      setOpenSelectTopBookingDialog(false)
    } catch (err) {
      console.error('Failed to fetch booking details:', err)
    }
  }

  const handleSelectTopBookingClick = () => {
    setOpenSelectTopBookingDialog(true)
  }

  return (
    <>
      <Tooltip title='Top Bookings' arrow>
        <Button
          variant='contained'
          color='grey'
          startIcon={<HistoryIcon />}
          disabled={!customerIdValue || topBookingList?.length === 0}
          onClick={handleSelectTopBookingClick}>
          History
        </Button>
      </Tooltip>

      {openSelectTopBookingDialog && (
        <SelectTopBookingDialog
          topBookingList={topBookingList}
          loadingTopBookings={loading}
          open={openSelectTopBookingDialog}
          setOpen={setOpenSelectTopBookingDialog}
          onSelectTopBooking={handleSelectTopBooking}
        />
      )}
    </>
  )
}
